/**
 * PreSessionTab Component
 * Contains RSVPs and Resources accordion sections for the session detail drawer.
 */

import { useState, useCallback } from 'react';
import { Stack, Button, Text } from '@mantine/core';
import { Plus } from 'react-feather';
import type { Session, ResourceCategory } from '../../../../types';
import { SectionAccordion } from './SectionAccordion';
import { RsvpSection } from './RsvpSection';
import { ResourcesSection } from './ResourcesSection';
import { AddResourceModal } from '../AddResourceModal';

interface PreSessionTabProps {
  cohortId: string;
  sessionId: string;
  sessions: Session[];
  categories: ResourceCategory[];
  onResourceAdded?: () => void;
  /**
   * MED-1360: hides Add Resource. The resource library routes are staff-only
   * (medley-api routes/resources.ts), so a coach can never add one.
   */
  readOnly?: boolean;
  /**
   * MED-1393: hides RSVPs for a 1:1. The booking sends the invite, but nearly
   * every status stays "pending" and partners' RSVP habits differ widely.
   */
  hideRsvps?: boolean;
  /** MED-1393: the resource count, so the 1:1 drawer can hide an empty tab. Pass a stable function. */
  onResourceCountChange?: (count: number) => void;
}

export function PreSessionTab({
  cohortId,
  sessionId,
  sessions,
  categories,
  onResourceAdded,
  readOnly = false,
  hideRsvps = false,
  onResourceCountChange,
}: PreSessionTabProps) {
  const [addModalOpened, setAddModalOpened] = useState(false);
  const [resourceKey, setResourceKey] = useState(0);
  const [resourceCount, setResourceCount] = useState(0);
  // Stable, because ResourcesSection reloads when onCountChange changes.
  const handleCount = useCallback(
    (count: number) => {
      setResourceCount(count);
      onResourceCountChange?.(count);
    },
    [onResourceCountChange],
  );

  const handleResourceCreated = () => {
    setResourceKey((k) => k + 1);
    onResourceAdded?.();
  };

  return (
    <Stack gap="sm">
      {!hideRsvps && (
        <SectionAccordion title="RSVPs" defaultExpanded>
          <RsvpSection cohortId={cohortId} sessionId={sessionId} />
        </SectionAccordion>
      )}

      {/* MED-1393: remount open after a save, so the new resource shows. */}
      <SectionAccordion
        key={resourceKey}
        title={
          <>
            Resources
            {resourceCount > 0 && (
              <Text span size="xs" c="dimmed" fw={400} ml={8}>
                {resourceCount} resource{resourceCount !== 1 ? 's' : ''}
              </Text>
            )}
          </>
        }
        defaultExpanded={resourceKey > 0}
        rightSection={
          readOnly ? undefined : (
            <Button
              size="xs"
              variant="light"
              leftSection={<Plus size={14} />}
              onClick={() => setAddModalOpened(true)}
            >
              Add Resource
            </Button>
          )
        }
      >
        <ResourcesSection
          key={resourceKey}
          cohortId={cohortId}
          sessionId={sessionId}
          categories={categories}
          onResourceAdded={handleResourceCreated}
          onCountChange={handleCount}
          readOnly={readOnly}
        />
      </SectionAccordion>

      {!readOnly && (
        <AddResourceModal
          opened={addModalOpened}
          onClose={() => setAddModalOpened(false)}
          cohortId={cohortId}
          sessions={sessions}
          categories={categories}
          onCreated={handleResourceCreated}
          defaultSessionIds={[sessionId]}
        />
      )}
    </Stack>
  );
}

export default PreSessionTab;
